import React, { useEffect } from "react";
import CustomPagination from "../../../components/CustomPagination";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { formatDate, formatSttCustomerCompany, formatDateForFileName, formatIdWithLength } from "../../../utils/format";
import { useSnackbar } from "notistack";
import CustomToast from "../../../components/CustomToast";
import {
    handleSortByHeader,
    TableHeader,
    setCurrentPage,
    setSuccess,
    setError,
    exportCompanyCsv,
} from "../../../reducers/customerCompany";
import i18n from 'i18next';

const CompanyList = ({ companyList }: any) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const {
        currentPage,
        countItems,
        pageSize,
        tableHeaders,
        error,
        success,
        actionName,
        sortBy,
        sortOrder,
        searchQuery,
        isSearch,
    } = useSelector((state: any) => state.customerCompany);

    const companyRecord = companyList.map((item: any) => ({
        ...item,
        contractStarted: formatDate(item.contract_started_at),
        contractDeadline: formatDate(item.expired_at),
        created_at: formatDate(item.created_at),
        status: formatSttCustomerCompany(item.is_contract_valid),
    }));

    const handleSort = (sortHeader: TableHeader) => {
        dispatch(handleSortByHeader(sortHeader));
    };

    const handleChangePage = (pageNumber: any) => {
        dispatch(setCurrentPage(pageNumber));
    };

    const handleExport = () => {
        const data = {
            searchQuery: searchQuery,
            sortBy: sortBy,
            sortOrder: sortOrder,
        }
        const params = new URLSearchParams(
            Object.entries(data).filter(
                ([_, value]) => value !== null && value !== undefined && value !== ''
            )
        );
        const url = `${window.location.origin}/control/api/company/export_csv?${params.toString()}`;
        dispatch(exportCompanyCsv(data))
            .unwrap()
            .then((data) => {
                if (data.success === false) {
                    return false;
                }
                window.open(url, "_blank");
                return;
            })
            .catch((error) => {
                return false;
            }
        );
    };

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    useEffect(() => {
        if(error) {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={error}
                    variant={"error"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setError(null));
        }
        else if(success && actionName == "deleteCustomerCompanyDetail") {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={success}
                    variant={"success"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setSuccess(null));
        }
    }, [success, error, actionName]);

    return (
        <>
        <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
            <div className="flex items-center justify-between">
                <CustomButton
                    type="button"
                    size="md"
                    onClick={() => navigate("/control/company/register")}
                >
                    新規登録
                </CustomButton>
                <CustomButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                >
                    CSVでダウンロード
                </CustomButton>
            </div>
        </div>
        {isSearch && companyRecord.length === 0
        ? <div className="bg-white mb-6 px-4 py-4">
            <p className="font-normal text-sm leading-[1.3125] alu-text-base-2">{i18n.t('MSG_GLOBAL_NO_SEARCH_RESULTS')}</p>
        </div>
        : <>
        <div className="bg-white mb-6">
            <table className="table-fixed w-full">
                <thead className="border-b alu-border px-4 py-2">
                    <tr>
                    {tableHeaders.map((header: TableHeader, index: number) => (
                        <th
                        key={index}
                        className={`px-4 py-2 text-left ${
                            header.customStyle ? header.customStyle : ""}`}
                        >
                        <div
                            className="flex items-center whitespace-pre-wrap"
                            onClick={() => header.isSort && handleSort(header)}
                        >
                            <p
                            className={`${
                                header.currentSortState !== 0
                                ? "font-bold underline underline-offset-4 alu-text-base-2"
                                : "font-normal alu-text-base-1"
                            } ${
                                header.isSort ? "hover:cursor-pointer" : ""
                            } text-sm leading-[1.3125]`}
                            >
                            {header.title}
                            </p>
                            {header.isSort && (
                            <button
                                className={`${
                                header.currentSortState === 2
                                    ? "rotate-180"
                                    : "rotate-0"
                                }`}
                            >
                                <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                >
                                <g clipPath="url(#clip0_8574_176)">
                                    <path d="M7 10L12 15L17 10H7Z" fill="#808080" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_8574_176">
                                    <rect width="24" height="24" fill="white" />
                                    </clipPath>
                                </defs>
                                </svg>
                            </button>
                            )}
                        </div>
                        </th>
                    ))}
                    </tr>
                </thead>
                <tbody>
                    {companyRecord.map((item: any, idx: number) => (
                    <tr
                        className="border-b border-b-[#E0E0E0] hover:bg-[#fff3e5]"
                        key={idx}
                    >
                        <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                            {formatIdWithLength(item.id)}
                        </td>
                        <td
                            onClick={() => navigate("/control/company/detail/" + item.id)}
                            className="min-w-[7rem] hover:cursor-pointer px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2"
                        >
                            {item.name}
                        </td>
                        <td className="break-words whitespace-normal px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                            {item.contractStarted}
                        </td>
                        <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                            {item.contractDeadline}
                        </td>
                        <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                            {item.status}
                        </td>
                        <td className="flex items-center">
                            <p className="pl-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 min-w-[80px]">{item.number_talent_register}</p>
                            <p
                                onClick={() => navigate("/control/company/detail/" + item.id)}
                                className="hover:cursor-pointer min-w-[80px] px-4 py-4 text-center font-normal text-sm leading-[1.3125] alu-text-title"
                            >詳細</p>
                            <p
                                onClick={() => navigate("/control/company/edit/" + item.id)}
                                className="hover:cursor-pointer min-w-[80px] px-4 py-4 text-center font-normal text-sm leading-[1.3125] alu-text-title"
                            >編集</p>
                        </td>
                    </tr>
                    ))}
                </tbody>
            </table>
        </div>
        {countItems > pageSize && (
        <div className="flex items-center justify-center mb-10">
            <CustomPagination 
                total={countItems} 
                currentPage={currentPage}
                pageSize={pageSize} 
                onPageChange={handleChangePage} 
                shape="circle"
            />
        </div>
        )}
        </>
        }
        </>
    );
};

export default CompanyList;
